import type { MerchantSpec, TransactionDescriptor } from "./spec";

/** A stored, curated MerchantSpec offered as a one-click starting point in the admin form. */
export interface BuiltInPreset {
  key: string;
  name: string;
  description: string;
  /** Cognito groups applied to the rolled identity on hydration. */
  groups: string[];
  spec: Omit<MerchantSpec, "identity">;
}

function activeMerchantTransactions(
  currency: "cad" | "usd",
): TransactionDescriptor[] {
  return [
    ...Array.from({ length: 7 }, (_, i) => ({
      targetStatus: "Succeeded" as const,
      amount: (i + 1) * 1500 + 500,
      currency,
      createdDaysAgo: 30 - i * 3,
      entryMode: (i % 2 === 0 ? "contactless" : "chip") as
        "contactless" | "chip",
      cardLast4: "4242",
    })),
    {
      targetStatus: "Refunded" as const,
      amount: 3500,
      currency,
      createdDaysAgo: 10,
      refundedDaysAgo: 8,
      refundedAmount: 3500,
      refundNote: "Customer requested refund",
      entryMode: "contactless" as const,
      cardLast4: "4242",
    },
    {
      targetStatus: "Failed" as const,
      amount: 5000,
      currency,
      createdDaysAgo: 5,
      entryMode: "chip" as const,
      cardLast4: "0002",
    },
    {
      targetStatus: "Voided" as const,
      amount: 2000,
      currency,
      createdDaysAgo: 3,
      entryMode: "contactless" as const,
      cardLast4: "4242",
    },
  ];
}

const activeMerchant: BuiltInPreset = {
  key: "activeMerchant",
  name: "Active merchant (CA)",
  description:
    "Fully onboarded Canadian merchant: verified, daily payouts, realistic transaction history, invoices, payouts, staff.",
  groups: ["Subscriber-TapToPay"],
  spec: {
    business: {
      name: "Maple Coffee",
      type: "individual",
      country: "CA",
      mcc: "5734",
      productDescription: "Point-of-sale payment processing (dev factory)",
      onboardingStatus: "completed",
      subscriptionStatus: "active",
      identityVerificationStatus: "verified",
      payoutInterval: "daily",
      profileParams: {},
    },
    stripe: {
      createConnectedAccount: true,
      subscription: { skip: false },
      restricted: false,
    },
    entities: {
      transactions: activeMerchantTransactions("cad"),
      invoices: [
        {
          amount: 5000,
          currency: "cad",
          status: "Paid",
          description: "Consulting services",
          recipientName: "Jane Customer",
          recipientContact: "jane@example.com",
          deliveryMethod: "email",
          createdDaysAgo: 20,
          dueInDays: -15,
          paidDaysAgo: 14,
        },
        {
          amount: 7500,
          currency: "cad",
          status: "Sent",
          description: "Monthly retainer",
          recipientName: "Bob Client",
          recipientContact: "bob@example.com",
          deliveryMethod: "email",
          createdDaysAgo: 5,
          dueInDays: 7,
        },
        {
          amount: 3000,
          currency: "cad",
          status: "Draft",
          description: "Website design",
          recipientName: "Alice Prospect",
          recipientContact: "+16475551234",
          deliveryMethod: "sms",
          createdDaysAgo: 1,
          dueInDays: 14,
        },
      ],
      payouts: [
        {
          amount: 15000,
          currency: "cad",
          status: "paid",
          createdDaysAgo: 7,
          arrivalInDays: 2,
        },
        {
          amount: 8000,
          currency: "cad",
          status: "in_transit",
          createdDaysAgo: 1,
          arrivalInDays: 3,
        },
      ],
      staff: [
        {
          name: "Shop Owner",
          email: "owner@cpos-seed.test",
          isOwner: true,
          isActive: true,
          pin: "1234",
        },
        {
          name: "Shop Cashier",
          email: "cashier@cpos-seed.test",
          isOwner: false,
          isActive: true,
          pin: "5678",
        },
      ],
      receiptsForFirstTransactions: 2,
    },
  },
};

const activeMerchantUS: BuiltInPreset = {
  key: "activeMerchantUS",
  name: "Active merchant (US)",
  description:
    "Fully onboarded US merchant: verified, daily payouts, realistic transaction history, invoices, payouts, staff.",
  groups: ["Subscriber-TapToPay"],
  spec: {
    business: {
      name: "Harbor Coffee",
      type: "individual",
      country: "US",
      mcc: "5734",
      productDescription: "Point-of-sale payment processing (dev factory)",
      onboardingStatus: "completed",
      subscriptionStatus: "active",
      identityVerificationStatus: "verified",
      payoutInterval: "daily",
      profileParams: {},
    },
    stripe: {
      createConnectedAccount: true,
      subscription: { skip: false },
      restricted: false,
    },
    entities: {
      transactions: activeMerchantTransactions("usd"),
      invoices: [
        {
          amount: 5000,
          currency: "usd",
          status: "Paid",
          description: "Consulting services",
          recipientName: "Jane Customer",
          recipientContact: "jane@example.com",
          deliveryMethod: "email",
          createdDaysAgo: 20,
          dueInDays: -15,
          paidDaysAgo: 14,
        },
        {
          amount: 7500,
          currency: "usd",
          status: "Sent",
          description: "Monthly retainer",
          recipientName: "Bob Client",
          recipientContact: "bob@example.com",
          deliveryMethod: "email",
          createdDaysAgo: 5,
          dueInDays: 7,
        },
        {
          amount: 3000,
          currency: "usd",
          status: "Draft",
          description: "Website design",
          recipientName: "Alice Prospect",
          recipientContact: "+12125559876",
          deliveryMethod: "sms",
          createdDaysAgo: 1,
          dueInDays: 14,
        },
      ],
      payouts: [
        {
          amount: 15000,
          currency: "usd",
          status: "paid",
          createdDaysAgo: 7,
          arrivalInDays: 2,
        },
        {
          amount: 8000,
          currency: "usd",
          status: "in_transit",
          createdDaysAgo: 1,
          arrivalInDays: 3,
        },
      ],
      staff: [
        {
          name: "Shop Owner",
          email: "owner-us@cpos-seed.test",
          isOwner: true,
          isActive: true,
          pin: "1234",
        },
        {
          name: "Shop Cashier",
          email: "cashier-us@cpos-seed.test",
          isOwner: false,
          isActive: true,
          pin: "5678",
        },
      ],
      receiptsForFirstTransactions: 2,
    },
  },
};

const trialMerchant: BuiltInPreset = {
  key: "trialMerchant",
  name: "Trial merchant",
  description:
    "Mid-onboarding Canadian merchant on a 14-day Stripe subscription trial with identity verification still pending.",
  groups: ["Subscriber-TapToPay"],
  spec: {
    business: {
      name: "Willow Bakery",
      type: "individual",
      country: "CA",
      mcc: "5734",
      productDescription: "Point-of-sale payment processing (dev factory)",
      onboardingStatus: "in_progress",
      subscriptionStatus: "trialing",
      identityVerificationStatus: "pending",
      payoutInterval: "manual",
      profileParams: {},
    },
    stripe: {
      createConnectedAccount: true,
      subscription: { skip: false, trialDays: 14 },
      restricted: false,
    },
    entities: {
      transactions: [],
      invoices: [],
      payouts: [],
      staff: [],
      receiptsForFirstTransactions: 0,
    },
  },
};

const pastDueMerchant: BuiltInPreset = {
  key: "pastDueMerchant",
  name: "Past-due merchant",
  description:
    "Fully onboarded Canadian company merchant whose Stripe subscription has lapsed into past_due.",
  groups: [],
  spec: {
    business: {
      name: "Northern Ridge Consulting",
      type: "company",
      country: "CA",
      mcc: "5734",
      productDescription: "Point-of-sale payment processing (dev factory)",
      onboardingStatus: "completed",
      subscriptionStatus: "past_due",
      identityVerificationStatus: "verified",
      payoutInterval: "daily",
      profileParams: {},
    },
    stripe: {
      createConnectedAccount: true,
      subscription: { skip: false },
      restricted: false,
    },
    entities: {
      transactions: Array.from({ length: 5 }, (_, i) => ({
        targetStatus: "Succeeded" as const,
        amount: (i + 1) * 2000,
        currency: "cad" as const,
        createdDaysAgo: 60 - i * 7,
        entryMode: "contactless" as const,
        cardLast4: "4242",
      })),
      invoices: [],
      payouts: [
        {
          amount: 20000,
          currency: "cad",
          status: "paid",
          createdDaysAgo: 45,
          arrivalInDays: 2,
        },
      ],
      staff: [],
      receiptsForFirstTransactions: 0,
    },
  },
};

const restrictedMerchant: BuiltInPreset = {
  key: "restrictedMerchant",
  name: "Restricted merchant",
  description:
    "Mid-onboarding Canadian merchant with an incomplete Stripe connected account so charges remain restricted.",
  groups: [],
  spec: {
    business: {
      name: "Copper Detailing",
      type: "individual",
      country: "CA",
      mcc: "5734",
      productDescription: "Point-of-sale payment processing (dev factory)",
      onboardingStatus: "in_progress",
      subscriptionStatus: "active",
      profileParams: {},
    },
    stripe: {
      createConnectedAccount: true,
      subscription: { skip: false },
      restricted: true,
    },
    entities: {
      transactions: [],
      invoices: [],
      payouts: [],
      staff: [],
      receiptsForFirstTransactions: 0,
    },
  },
};

const unverifiedMerchant: BuiltInPreset = {
  key: "unverifiedMerchant",
  name: "Unverified merchant",
  description:
    "Fully subscribed Canadian merchant whose identity verification is still pending, gating payouts to manual.",
  groups: ["Subscriber-TapToPay"],
  spec: {
    business: {
      name: "Silver Massage Studio",
      type: "individual",
      country: "CA",
      mcc: "5734",
      productDescription: "Point-of-sale payment processing (dev factory)",
      onboardingStatus: "completed",
      subscriptionStatus: "active",
      identityVerificationStatus: "pending",
      payoutInterval: "manual",
      profileParams: {},
    },
    stripe: {
      createConnectedAccount: true,
      subscription: { skip: false },
      restricted: false,
    },
    entities: {
      transactions: [],
      invoices: [],
      payouts: [],
      staff: [],
      receiptsForFirstTransactions: 0,
    },
  },
};

const newMerchant: BuiltInPreset = {
  key: "newMerchant",
  name: "New merchant",
  description:
    "Fully onboarded, verified Canadian merchant with zero transaction history, for exercising empty states.",
  groups: ["Subscriber-TapToPay"],
  spec: {
    business: {
      name: "Sunset Fitness",
      type: "individual",
      country: "CA",
      mcc: "5734",
      productDescription: "Point-of-sale payment processing (dev factory)",
      onboardingStatus: "completed",
      subscriptionStatus: "active",
      identityVerificationStatus: "verified",
      payoutInterval: "daily",
      profileParams: {},
    },
    stripe: {
      createConnectedAccount: true,
      subscription: { skip: false },
      restricted: false,
    },
    entities: {
      transactions: [],
      invoices: [],
      payouts: [],
      staff: [],
      receiptsForFirstTransactions: 0,
    },
  },
};

const terminalMerchant: BuiltInPreset = {
  key: "terminalMerchant",
  name: "Terminal merchant",
  description:
    "Admin-provisioned Poynt terminal merchant that bypasses the Hub subscription flow, with two PIN-authenticated staff.",
  groups: ["Subscriber-PoyntTerminal"],
  spec: {
    business: {
      name: "Iron Repair Shop",
      type: "individual",
      country: "CA",
      mcc: "5734",
      productDescription: "Point-of-sale payment processing (dev factory)",
      onboardingStatus: "in_progress",
      subscriptionStatus: "inactive",
      identityVerificationStatus: "verified",
      profileParams: {},
    },
    stripe: {
      createConnectedAccount: true,
      subscription: { skip: true },
      restricted: false,
    },
    entities: {
      transactions: [
        {
          targetStatus: "Succeeded",
          amount: 4250,
          currency: "cad",
          createdDaysAgo: 1,
          entryMode: "chip",
          cardLast4: "4242",
        },
        {
          targetStatus: "Succeeded",
          amount: 2899,
          currency: "cad",
          createdDaysAgo: 2,
          entryMode: "contactless",
          cardLast4: "4242",
        },
        {
          targetStatus: "Succeeded",
          amount: 1875,
          currency: "cad",
          createdDaysAgo: 3,
          entryMode: "chip",
          cardLast4: "4242",
        },
        {
          targetStatus: "Succeeded",
          amount: 6500,
          currency: "cad",
          createdDaysAgo: 5,
          entryMode: "contactless",
          cardLast4: "4242",
        },
        {
          targetStatus: "Succeeded",
          amount: 3120,
          currency: "cad",
          createdDaysAgo: 6,
          entryMode: "chip",
          cardLast4: "4242",
        },
      ],
      invoices: [],
      payouts: [],
      staff: [
        {
          name: "Terminal Owner",
          email: "terminal-owner@cpos-seed.test",
          isOwner: true,
          isActive: true,
          pin: "1234",
        },
        {
          name: "Terminal Cashier",
          email: "terminal-cashier@cpos-seed.test",
          isOwner: false,
          isActive: true,
          pin: "5678",
        },
      ],
      receiptsForFirstTransactions: 1,
    },
  },
};

const entitlementsRefreshMerchant: BuiltInPreset = {
  key: "entitlementsRefreshMerchant",
  name: "Entitlements refresh merchant",
  description:
    "Fully onboarded but inactive Canadian merchant with a skipped subscription, for exercising token-refresh entitlement flips.",
  groups: ["Subscriber-TapToPay"],
  spec: {
    business: {
      name: "Golden Ledger Bookkeeping",
      type: "individual",
      country: "CA",
      mcc: "5734",
      productDescription: "Point-of-sale payment processing (dev factory)",
      onboardingStatus: "completed",
      subscriptionStatus: "inactive",
      identityVerificationStatus: "verified",
      payoutInterval: "daily",
      profileParams: {},
    },
    stripe: {
      createConnectedAccount: true,
      subscription: { skip: true },
      restricted: false,
    },
    entities: {
      transactions: [],
      invoices: [],
      payouts: [],
      staff: [],
      receiptsForFirstTransactions: 0,
    },
  },
};

export const BUILT_IN_PRESETS: BuiltInPreset[] = [
  activeMerchant,
  activeMerchantUS,
  trialMerchant,
  pastDueMerchant,
  restrictedMerchant,
  unverifiedMerchant,
  newMerchant,
  terminalMerchant,
  entitlementsRefreshMerchant,
];
